import { Link } from "react-router-dom";
import { PublicHeader } from "@/components/marketing/PublicHeader";
import { PublicFooter } from "@/components/marketing/PublicFooter";
import { FeatureCard } from "@/components/marketing/FeatureCard";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import {
  FileText,
  Search,
  GitCompare,
  ListChecks,
  CreditCard,
  CheckCircle2,
  Shield,
  MessageSquare,
  ArrowRight,
  Sparkles,
  Bot,
} from "lucide-react";

export default function ForClients() {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="For Buyers - Streamline Procurement"
        description="Transform your procurement process with AI-powered RFQ creation, vendor discovery, bid comparison, milestone tracking, and secure payments."
        canonical="/for-clients"
      />
      <PublicHeader />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 via-background to-background">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.1),transparent_50%)]" />
        <div className="container relative py-12 md:py-20">
          <div className="mx-auto max-w-4xl text-center space-y-8">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
            Streamline Your <span className="text-primary">Procurement Process</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            From RFQ creation to final delivery, manage your entire procurement workflow with confidence and transparency.
          </p>
          <Button size="lg" asChild>
            <Link to="/auth/signup?type=buyer">
              Start Your First RFQ <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          </div>
        </div>
      </section>

      {/* Pain Points */}
      <section className="bg-muted/50 py-16">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">Challenges We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center space-y-2">
              <h3 className="font-semibold">Finding the Right Vendors</h3>
              <p className="text-sm text-muted-foreground">
                Stop wasting time searching. Browse vendors with verified business credentials and documented capabilities.
              </p>
            </div>
            <div className="text-center space-y-2">
              <h3 className="font-semibold">Managing Multiple Bids</h3>
              <p className="text-sm text-muted-foreground">
                Compare bids side-by-side with clear line-item breakdowns and make informed decisions quickly.
              </p>
            </div>
            <div className="text-center space-y-2">
              <h3 className="font-semibold">Payment & Delivery Risk</h3>
              <p className="text-sm text-muted-foreground">
                Secure payments with delivery verification protect you at every step of the project.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* AI Feature Highlight */}
      <section className="container py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
              <Sparkles className="h-4 w-4" />
              AI-Powered Innovation
            </div>
            <h2 className="text-3xl font-bold">Build Specifications Through Conversation</h2>
            <p className="text-lg text-muted-foreground">
              Forget complex forms and confusing templates. Simply describe your project in plain English, 
              and our AI assistant guides you through creating professional, industry-standard specifications.
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <span><strong>No expertise required</strong>—AI asks the right questions</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <span><strong>5x faster</strong> than traditional spec creation</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <span><strong>Review before publishing</strong>—you stay in control</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <span><strong>Accurate bids</strong>—vendors get the details they need</span>
              </li>
            </ul>
          </div>
          <div className="relative">
            <div className="rounded-2xl border bg-card p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-4 pb-4 border-b">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Bot className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <div className="font-semibold">AI Specification Assistant</div>
                  <div className="text-sm text-muted-foreground">Guided conversation</div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Bot className="h-4 w-4 text-primary" />
                  </div>
                  <div className="bg-muted rounded-lg px-4 py-2 text-sm">
                    What type of packaging are you looking for? I can help with folding cartons, rigid boxes, corrugated, flexible packaging, and more.
                  </div>
                </div>
                <div className="flex gap-3 justify-end">
                  <div className="bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm max-w-xs">
                    I need custom folding cartons for a new product launch. About 10,000 units.
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Bot className="h-4 w-4 text-primary" />
                  </div>
                  <div className="bg-muted rounded-lg px-4 py-2 text-sm">
                    Great choice! Can you tell me about the product dimensions so I can suggest the right carton size?
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features for Buyers */}
      <section className="container py-20">
        <div className="text-center space-y-4 mb-12">
          <h2 className="text-3xl font-bold">Powerful Tools for Buyers</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Everything you need to manage procurement from start to finish
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <FeatureCard
            icon={Sparkles}
            title="AI Specification Builder"
            description="Describe your project in plain English—our AI creates professional specifications through guided conversation."
          />
          <FeatureCard
            icon={FileText}
            title="Detailed RFQ Creation"
            description="Build comprehensive RFQs with custom specifications, quantity options, delivery locations, and attachments."
          />
          <FeatureCard
            icon={Search}
            title="Vendor Discovery"
            description="Search and filter vendors by capabilities, verified certifications, locations, and platform ratings. Review transaction history and client feedback before inviting to bid."
          />
          <FeatureCard
            icon={GitCompare}
            title="Bid Comparison"
            description="Review all bids in a unified view with line-item breakdowns, quantity pricing, and vendor profiles."
          />
          <FeatureCard
            icon={ListChecks}
            title="Milestone Management"
            description="Break projects into manageable milestones with clear deliverables, deadlines, and payment schedules. Automated reminders and completion tracking keep everyone informed and on track."
          />
          <FeatureCard
            icon={CreditCard}
            title="Secure Payment Processing"
            description="Stripe-powered payments with funds protected until milestone completion and verification."
          />
          <FeatureCard
            icon={CheckCircle2}
            title="Delivery Verification"
            description="Review vendor-submitted proof of delivery with photos, documents, and custom templates before releasing payment."
          />
          <FeatureCard
            icon={MessageSquare}
            title="Direct Communication"
            description="Built-in messaging with vendors to clarify requirements, answer questions, and coordinate delivery."
          />
          <FeatureCard
            icon={Shield}
            title="Dispute Resolution"
            description="Platform-mediated dispute resolution process protects both parties with fair outcomes."
          />
        </div>
      </section>

      {/* Use Cases */}
      <section className="bg-muted/50 py-20">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">Perfect For</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="p-6 rounded-lg border bg-background">
              <h3 className="text-xl font-semibold mb-3">Marketing & Brand Teams</h3>
              <p className="text-sm text-muted-foreground">
                Produce branded print materials, packaging, signage, and promotional items with precise specifications and brand compliance.
              </p>
            </div>
            <div className="p-6 rounded-lg border bg-background">
              <h3 className="text-xl font-semibold mb-3">Event & Trade Show Managers</h3>
              <p className="text-sm text-muted-foreground">
                Source booth graphics, banners, giveaways, and custom event materials with deadline-driven project management.
              </p>
            </div>
            <div className="p-6 rounded-lg border bg-background">
              <h3 className="text-xl font-semibold mb-3">Retail & CPG Brands</h3>
              <p className="text-sm text-muted-foreground">
                Manage packaging production, point-of-sale displays, and promotional merchandise across multiple vendors.
              </p>
            </div>
            <div className="p-6 rounded-lg border bg-background">
              <h3 className="text-xl font-semibold mb-3">Corporate Procurement</h3>
              <p className="text-sm text-muted-foreground">
                Centralize purchasing for branded merchandise, office signage, sales collateral, and employee recognition programs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="container py-20">
        <h2 className="text-3xl font-bold text-center mb-12">Buyer Journey</h2>
        <div className="max-w-3xl mx-auto space-y-8">
            {[
            {
              step: "1",
              title: "Describe Your Project to AI",
              description: "Simply describe what you need in plain English. Our AI assistant guides you through creating professional specifications—no forms required."
            },
            {
              step: "2",
              title: "Invite or Discover Vendors",
              description: "Choose to invite specific vendors you've worked with before, or open your RFQ to vendors in our marketplace. Filter by verified credentials, certifications, and capabilities."
            },
            {
              step: "3",
              title: "Review & Compare Bids",
              description: "Receive detailed bids with line-item pricing. Compare vendors side-by-side, review their platform ratings, verified credentials, and past reviews."
            },
            {
              step: "4",
              title: "Award & Set Up Payment",
              description: "Select the winning bid and set up secure payment through Stripe. Your payment is protected until milestones are completed."
            },
            {
              step: "5",
              title: "Track Project Progress",
              description: "Monitor milestone completion in real-time. Communicate with vendors, track shipments, and manage documentation all in one place."
            },
            {
              step: "6",
              title: "Verify Delivery & Release Payment",
              description: "Review vendor-submitted proof of delivery. Once satisfied, approve the milestone and payment is automatically released."
            },
            {
              step: "7",
              title: "Leave a Review",
              description: "Help other buyers by rating your experience and leaving feedback about the vendor's performance."
            },
          ].map((item) => (
            <div key={item.step} className="flex gap-6">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xl font-bold">
                {item.step}
              </div>
              <div className="pt-2 space-y-2">
                <h3 className="text-xl font-semibold">{item.title}</h3>
                <p className="text-muted-foreground">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-y bg-primary/5 py-20">
        <div className="container text-center space-y-6">
          <h2 className="text-3xl font-bold">Ready to Transform Your Procurement?</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Join hundreds of companies streamlining their procurement process with Radius.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild>
              <Link to="/auth/signup?type=buyer">
                Get Started Free
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/how-it-works/clients">
                Learn how it works for buyers
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
